import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

const CONNECTIVITY_CHECK_INTERVAL = 30000; // Check every 30 seconds
const CONNECTIVITY_CHECK_TIMEOUT = 5000; // 5 second timeout

interface OnlineStatusContextType {
  isOnline: boolean;
  isOffline: boolean;
  justReconnected: boolean;
}

const OnlineStatusContext = createContext<OnlineStatusContextType | undefined>(undefined);

export function OnlineStatusProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const isCheckingRef = useRef(false);

  useEffect(() => {
    // Actively check internet connectivity by making a fetch request
    const checkConnectivity = async () => {
      if (isCheckingRef.current) return;

      isCheckingRef.current = true;
      try {
        // Try to fetch a small resource with no-cache to verify actual internet access
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), CONNECTIVITY_CHECK_TIMEOUT);

        const response = await fetch('/favicon.ico', {
          method: 'HEAD',
          cache: 'no-cache',
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        const online = response.ok;

        setIsOnline(prev => {
          if (online !== prev) {
            if (!online) {
              setWasOffline(true);
            } else {
              // Show reconnected message briefly
              setTimeout(() => setWasOffline(false), 3000);
            }
            return online;
          }
          return prev;
        });
      } catch (error) {
        // If fetch fails, we're likely offline
        setIsOnline(prev => {
          if (prev) {
            setWasOffline(true);
            return false;
          }
          return prev;
        });
      } finally {
        isCheckingRef.current = false;
      }
    };

    const handleOnline = () => {
      // Browser thinks we're online, but verify with actual check
      checkConnectivity();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
    };

    // Initial connectivity check
    checkConnectivity();

    // Listen to browser events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also check on visibility change (when tab becomes active)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkConnectivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic connectivity check - ONLY ONE INTERVAL for entire app
    const intervalId = setInterval(checkConnectivity, CONNECTIVITY_CHECK_INTERVAL);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, []); // Empty deps - only runs once for entire app

  return (
    <OnlineStatusContext.Provider value={{
      isOnline,
      isOffline: !isOnline,
      justReconnected: isOnline && wasOffline
    }}
    >
      {children}
    </OnlineStatusContext.Provider>
  );
}

export function useOnlineStatus(): OnlineStatusContextType {
  const context = useContext(OnlineStatusContext);
  if (context === undefined) {
    throw new Error('useOnlineStatus must be used within OnlineStatusProvider');
  }
  return context;
}
